/**
 * Prompts API
 * პრომპტების API
 */

import { get, post, put, del } from '@aiia/portal-core';

export interface RagConfig {
  enabled: boolean;
  chunkSize: number;
  chunkOverlap: number;
  topK: number;
  similarityThreshold: number;
}

export interface DataFileSummary {
  id: string;
  fileName: string;
  fileType?: string;
  fileSize?: number;
  status?: string;
  vectorStoreId?: string;
  displayOrder: number;
}

export interface Prompt {
  id: string;
  userId: string;
  name: string;
  description?: string;
  systemPrompt: string;
  model: string;
  temperature: number;
  maxTokens: number;
  ragConfig?: RagConfig;
  files?: DataFileSummary[];
  isActive: boolean;
  agentMode: boolean;
  maxIterations: number;
  createdAt: string;
  updatedAt: string;
}

export interface PromptSummary {
  id: string;
  name: string;
  description?: string;
  model: string;
  isActive: boolean;
  fileCount: number;
  ragEnabled: boolean;
}

export interface CreatePromptRequest {
  name: string;
  description?: string;
  systemPrompt: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  agentMode?: boolean;
  maxIterations?: number;
}

export interface UpdatePromptRequest extends Partial<CreatePromptRequest> {}

export interface UpdateRagConfigRequest {
  enabled: boolean;
  topK?: number;
  similarityThreshold?: number;
}

// Available AI models
export const AI_MODELS = [
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Recommended)' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
];

// Get all prompts
export const getPrompts = () =>
  get<Prompt[]>('/prompts');

// Get prompt summaries
export const getPromptSummaries = () =>
  get<PromptSummary[]>('/prompts/summaries');

// Get prompt by ID
export const getPrompt = (id: string) =>
  get<Prompt>(`/prompts/${id}`);

// Create prompt
export const createPrompt = (data: CreatePromptRequest) =>
  post<Prompt>('/prompts', data);

// Update prompt
export const updatePrompt = (id: string, data: UpdatePromptRequest) =>
  put<Prompt>(`/prompts/${id}`, data);

// Update RAG config
export const updateRagConfig = (id: string, config: UpdateRagConfigRequest) =>
  put<Prompt>(`/prompts/${id}/rag-config`, config);

// Activate prompt
export const activatePrompt = (id: string) =>
  put<Prompt>(`/prompts/${id}/activate`, {});

// Deactivate prompt
export const deactivatePrompt = (id: string) =>
  put<Prompt>(`/prompts/${id}/deactivate`, {});

// Add file to prompt
export const addFileToPrompt = (promptId: string, fileId: string) =>
  post<Prompt>(`/prompts/${promptId}/files/${fileId}`, {});

// Remove file from prompt
export const removeFileFromPrompt = (promptId: string, fileId: string) =>
  del<void>(`/prompts/${promptId}/files/${fileId}`);

// Delete prompt
export const deletePrompt = (id: string) =>
  del<void>(`/prompts/${id}`);
